import { useState } from 'react';

import { formatPeriod } from '../../components/EngagementHub/format';
import { formatHours } from '../money';
import type { HoursSubmissionView } from '../viewModel';
import CurrentPeriodTrigger from './CurrentPeriodTrigger';
import HoursFullWidthCell from './HoursFullWidthCell';
import HoursStatusCell from './HoursStatusCell';
import { CELL, DASH, MUTED_CELL, MUTED_DASH, hoursColumns } from './HoursTable';
import SubmitHoursForm, { type Surplus } from './SubmitHoursForm';

interface CurrentPeriodRowProps {
  /** The api's render-only `not_yet_submitted` row. */
  row: HoursSubmissionView;
  engagementId: string;
  showPaid: boolean;
}

/** `closed` means the form has been open, so the returning button takes focus back. */
type FormState = 'unopened' | 'open' | 'closed';

/**
 * The expert's first row: the current pacing period, not yet submitted. The
 * prototype puts its Submit hours button in the Description cell; here it sits
 * in a full-width row beneath, like every row's actions, so it stays in view
 * when the table scrolls sideways on a phone. The form opens in its place.
 *
 * The api names the period only while the engagement is active and nothing
 * submitted covers it. Otherwise, while closing or once the period is covered,
 * the row stays without a period or a pill, so the expert can still submit
 * for another one.
 *
 * The row, not the form, holds the surplus banner. A submission split at
 * NTE-remaining went through, so closing the form must not take the only
 * notice of what didn't fit with it; the banner clears when the next
 * submission starts.
 */
export default function CurrentPeriodRow({
  row,
  engagementId,
  showPaid,
}: CurrentPeriodRowProps): JSX.Element {
  const [form, setForm] = useState<FormState>('unopened');
  const [surplus, setSurplus] = useState<Surplus | null>(null);
  const period =
    row.periodStart && row.periodEnd ? { start: row.periodStart, end: row.periodEnd } : undefined;

  return (
    <>
      <tr className="bg-surface-elevated align-top">
        <td className={`${CELL} whitespace-nowrap`}>
          {period ? formatPeriod(period.start, period.end) : MUTED_DASH}
        </td>
        <td className={`${CELL} text-right`}>{MUTED_DASH}</td>
        <td className={`${CELL} text-right`}>{MUTED_DASH}</td>
        <td className={CELL}>{period ? <HoursStatusCell row={row} /> : MUTED_DASH}</td>
        <td className={`${CELL} min-w-[240px] max-w-[420px]`}>
          <span className="italic text-on-surface-muted body-2">
            {period ? 'Submit hours for this period.' : 'Submit hours for another period.'}
          </span>
        </td>
        <td className={MUTED_CELL}>{DASH}</td>
        {showPaid ? <td className={MUTED_CELL}>{DASH}</td> : null}
      </tr>
      <tr
        className={`border-b border-surface-border ${form === 'open' ? '' : 'bg-surface-elevated'}`}
      >
        <HoursFullWidthCell colSpan={hoursColumns(showPaid).length}>
          <div className="flex flex-col gap-12">
            {surplus ? (
              <p className="rounded-md border border-yellow-700/40 bg-yellow-50 px-12 py-8 text-on-surface body-2 dark:bg-yellow-900/10">
                {surplusMessage(surplus)}
              </p>
            ) : null}
            {form === 'open' ? (
              <SubmitHoursForm
                engagementId={engagementId}
                period={period}
                onClose={() => setForm('closed')}
                onSurplusChange={setSurplus}
              />
            ) : (
              <CurrentPeriodTrigger
                focusOnMount={form === 'closed'}
                onOpen={() => setForm('open')}
              />
            )}
          </div>
        </HoursFullWidthCell>
      </tr>
    </>
  );
}

/**
 * One text node, not a conditional beside static copy: the latter is what
 * Google Translate trips over (`react-google-translate`).
 */
function surplusMessage(surplus: Surplus): string {
  return (
    `${surplus.hours === null ? 'Some hours' : formatHours(surplus.hours)} weren't accepted. ` +
    'They would take this engagement past its not-to-exceed hours. Propose an amendment to ' +
    'extend the hours, then submit the rest.'
  );
}
